import React, { useEffect, useState } from 'react';
import InputField from '../Reusable/InputField';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  Box,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ComboReusable from '../Reusable/ComboReusable';
import { Placeholders } from '../../enums/ProductEnums';
import { SubmitButton } from '../../enums/GlobalEnums';
import { useDispatch, useSelector } from 'react-redux';
import { findAllProductCategorys } from '../../actions/productCategory';
import { ModalForAddOrUpdateProduct } from '../../enums/ProductEnums';
import CaseLogo from '../Reusable/Upload-Latest/CaseLogo';

const ModalForUpdateProduct = ({
  handleClose,
  handleEdit,
  show,
  productId,
  findProductByIdWithLogo,
  updateProductWithLogo,
}) => {
  const dispatch = useDispatch();
  const { error, logoLoading } = useSelector((state) => state.product);

  const productCategoryReducer = useSelector((state) => state.productCategory);
  const { productCategoryList } = productCategoryReducer;
  const productReducer = useSelector((state) => state.product);
  const { productWithLogo } = productReducer;

  const [editProduct, setEditProduct] = useState({
    id: '',
    productName: '',
    price: '',
    description: '',
    productCategory: {
      id: null,
    },
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [hasImageChanged, setHasImageChanged] = useState(false);

  useEffect(() => {
    if (productId && show) {
      dispatch(findProductByIdWithLogo(productId));
    }
    dispatch(findAllProductCategorys());
  }, [dispatch, productId, show]);

  useEffect(() => {
    if (show && productWithLogo && productWithLogo.id) {
      setEditProduct({
        id: productWithLogo.id,
        productName: productWithLogo.productName || '',
        price: productWithLogo.price || '',
        description: productWithLogo.description || '',
        productCategory: productWithLogo.productCategory || { id: null },
      });

      if (productWithLogo.logoUrl) {
        setSelectedImage({
          id: `existing_${productWithLogo.id}`,
          name:
            productWithLogo.logoName || `${productWithLogo.productName}_logo`,
          preview: `${window.location.origin}${productWithLogo.logoUrl}`, 
          isExisting: true,
          uuid: productWithLogo.logoUuid,
        });
      } else {
        setSelectedImage(null);
      }
      setHasImageChanged(false);
    }
  }, [productWithLogo, show]);

  const onChange = (e) => {
    setEditProduct({ ...editProduct, [e.target.name]: e.target.value });
  };

  const handleComboBoxChange = (object, objectId) => {
    return (e) => {
      setEditProduct((prevState) => ({
        ...prevState,
        [object]: { [objectId]: e.target.value },
      }));
    };
  };

  const handleFileChangeLogo = (e) => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();

      reader.onload = (event) => {
        setSelectedImage({
          id: `new_${Date.now()}`,
          file: file,
          name: file.name,
          preview: event.target.result,
          size: file.size,
          isExisting: false,
          isNew: true,
        });
        setHasImageChanged(true);
      };

      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = () => {
    setSelectedImage(null);
    setHasImageChanged(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const productData = {
      id: productWithLogo.id,
      ...editProduct,
    };

    if (
      hasImageChanged &&
      selectedImage &&
      selectedImage.file &&
      selectedImage.isNew
    ) {
      let formData = new FormData();
      formData.append('productLogoImage', selectedImage.file);
      formData.append(
        'product',
        new Blob([JSON.stringify(productData)], {
          type: 'application/json',
        })
      );

      updateProductWithLogo(formData);
    } else {
      handleEdit(productData);
    }
  };

  return (
    <>
      <Dialog
        open={show}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            padding: '25px', // Border thickness
            borderRadius: '20px', // Rounded outer border
            background: '#7C9082',
            display: 'inline-block',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#fff', // Inner content background
              padding: '24px',
              borderRadius: '0px', // Sharp corners inside
            }}
          >
            <DialogTitle>
              {ModalForAddOrUpdateProduct.titleForUpdateProduct}
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{ position: 'absolute', right: 55, top: 55 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider
              sx={{
                border: '1px solid #7C9082 !important',
              }}
            />
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={5}>
                  <CaseLogo
                    onFileChange={handleFileChangeLogo}
                    selectedImage={selectedImage}
                    onDeleteImage={handleDeleteImage}
                  />
                </Grid>
                <Grid item xs={7}>
                  <InputField
                    name="productName"
                    placeholder={Placeholders.productName}
                    onChange={onChange}
                    value={editProduct.productName}
                    autoComplete="off"
                    error={error?.productName}
                  />

                  <InputField
                    name="price"
                    placeholder={Placeholders.price}
                    onChange={onChange}
                    value={editProduct.price}
                    autoComplete="off"
                    error={error?.price}
                  />

                  <InputField
                    name="description"
                    placeholder={Placeholders.description}
                    onChange={onChange}
                    value={editProduct.description}
                    autoComplete="off"
                    error={error?.description}
                  />

                  <ComboReusable
                    id="productCategory"
                    updateValue={editProduct?.productCategory?.id}
                    items={productCategoryList.map((objekat) => (
                      <option key={objekat.id} value={objekat.id}>
                        {objekat.productCategoryName}
                      </option>
                    ))}
                    onChange={handleComboBoxChange('productCategory', 'id')}
                  />
                  <DialogActions>
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      disabled={logoLoading}
                      sx={{
                        backgroundColor: '#7C9082',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: '#7C9082',
                        },
                      }}
                    >
                      {logoLoading ? 'Updating...' : SubmitButton.update}
                    </Button>
                  </DialogActions>
                </Grid>
              </Grid>
            </DialogContent>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default ModalForUpdateProduct;
